import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SelectComponent } from '@shared/components/select';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';

/** Choix par defaut, repris du paginateur Material d'origine. */
export const TAILLES_PAGE_PAR_DEFAUT: readonly number[] = [25, 50, 75, 100];

/**
 * Selecteur de nombre de lignes par page, a poser a cote d'`app-pagination-control`.
 *
 * `app-pagination-control` prend `pageSize` en entree FIGEE et n'expose aucune
 * sortie pour le changer : le paginateur Material offrait `[pageSizeOptions]`,
 * pas lui. Comme le paginateur du kit est un simple element frere dans le
 * gabarit, le manque se comble entierement cote consommateur — aucune
 * divergence du kit.
 *
 * ⚠️ Le consommateur DOIT remettre l'index de page a zero en reagissant a
 * `tailleChange` : rester page 8 en passant de 25 a 100 lignes envoie au-dela de
 * la derniere page, et la liste revient vide sans message.
 */
@Component({
  selector: 'app-taille-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectComponent],
  template: `
    <div class="flex items-center gap-2">
      <span class="text-xs text-content-tertiary">Lignes par page</span>
      <app-select
        label="Lignes par page"
        trigger="field"
        selectionMode="single"
        [options]="optionsSelect()"
        [selected]="selection()"
        (selectionChange)="choisir($event)"
      />
    </div>
  `,
})
export class TaillePageComponent {
  readonly taille = input.required<number>();
  readonly options = input<readonly number[]>(TAILLES_PAGE_PAR_DEFAUT);

  readonly tailleChange = output<number>();

  /* `app-select` raisonne en CLES (chaines), pas en objets ni en nombres — cf. le
     manque A5 du registre. D'ou l'aller-retour string/number aux bords. */
  protected readonly optionsSelect = computed<readonly SelectOption[]>(() =>
    this.options().map(n => ({ value: String(n), label: String(n) })),
  );

  protected readonly selection = computed<readonly string[]>(() => [String(this.taille())]);

  protected choisir(valeurs: string[]): void {
    const choisie = Number(valeurs[0]);
    /* Le kit ne deselectionne pas au reclic en mode `single`, mais la croix
       d'effacement (divergence A29) peut vider le champ : on ignore alors, une
       taille de page nulle n'a pas de sens. */
    if (!Number.isFinite(choisie) || choisie <= 0) return;
    if (choisie === this.taille()) return;
    this.tailleChange.emit(choisie);
  }
}
